import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit'
import { Listing, ListingFilters } from '@/types/listing'
import { ListingType } from '@/data/listingSchema'
import { CreateListingPayload } from '@/lib/validations/listing'
import * as listingAPI from '@/lib/api/listings'

interface ListingState {
  listings: Listing[]
  searchResults: Listing[]
  currentListing: Listing | null
  filters: ListingFilters
  loading: boolean
  error: string | null
  favorites: string[]
  searchQuery: string
  totalCount: number
  viewMode: 'list' | 'grid' | 'map'
  selectedListingType: ListingType | 'all'
  currentDraft: Listing | null
  draftLoading: boolean
}

export const createListing = createAsyncThunk(
  'listing/create',
  async (listingData: CreateListingPayload, { rejectWithValue }) => {
    try {
      const response = await listingAPI.createListing(listingData)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to create listing')
    }
  }
)

export const fetchListings = createAsyncThunk(
  'listing/fetchAll',
  async (_, { rejectWithValue }) => {
    try {
      const response = await listingAPI.getListings()
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch listings')
    }
  }
)

export const fetchListingById = createAsyncThunk(
  'listing/fetchById',
  async (id: string, { rejectWithValue }) => {
    try {
      const response = await listingAPI.getListingById(id)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch listing')
    }
  }
)

export const updateListing = createAsyncThunk(
  'listing/update',
  async ({ id, data }: { id: string; data: Partial<CreateListingPayload> }, { rejectWithValue }) => {
    try {
      const response = await listingAPI.updateListing(id, data)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update listing')
    }
  }
)

export const saveDraft = createAsyncThunk(
  'listing/saveDraft',
  async (draftData: CreateListingPayload, { rejectWithValue }) => {
    try {
      const draftPayload = { ...draftData, status: 'draft' }
      const response = await listingAPI.createListing(draftPayload)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to save draft')
    }
  }
)

export const updateDraft = createAsyncThunk(
  'listing/updateDraft',
  async ({ id, data }: { id: string; data: Partial<CreateListingPayload> }, { rejectWithValue }) => {
    try {
      const draftPayload = { ...data, status: 'draft' }
      const response = await listingAPI.updateListing(id, draftPayload)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to update draft')
    }
  }
)

export const publishDraft = createAsyncThunk(
  'listing/publishDraft',
  async ({ id, data }: { id: string; data: Partial<CreateListingPayload> }, { rejectWithValue }) => {
    try {
      const publishPayload = { ...data, status: 'active' }
      const response = await listingAPI.updateListing(id, publishPayload)
      return response
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to publish listing')
    }
  }
)

export const removeListing = createAsyncThunk(
  'listing/delete',
  async (id: string, { rejectWithValue }) => {
    try {
      await listingAPI.deleteListing(id)
      return id
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to delete listing')
    }
  }
)

const initialState: ListingState = {
  listings: [],
  searchResults: [],
  currentListing: null,
  filters: {},
  loading: false,
  error: null,
  favorites: [],
  searchQuery: '',
  totalCount: 0,
  viewMode: 'grid',
  selectedListingType: 'all',
  currentDraft: null,
  draftLoading: false
}

export const listingSlice = createSlice({
  name: 'listing',
  initialState,
  reducers: {
    setListings: (state, action: PayloadAction<Listing[]>) => {
      state.listings = action.payload
      state.searchResults = action.payload
      state.totalCount = action.payload.length
    },
    setSearchResults: (state, action: PayloadAction<Listing[]>) => {
      state.searchResults = action.payload
      state.totalCount = action.payload.length
    },
    setCurrentListing: (state, action: PayloadAction<Listing | null>) => {
      state.currentListing = action.payload
    },
    setFilters: (state, action: PayloadAction<ListingFilters>) => {
      state.filters = action.payload
    },
    updateFilter: (state, action: PayloadAction<{ key: keyof ListingFilters; value: unknown }>) => {
      state.filters = {
        ...state.filters,
        [action.payload.key]: action.payload.value
      }
    },
    clearFilters: (state) => {
      state.filters = {}
      state.searchQuery = ''
      state.selectedListingType = 'all'
    },
    setSearchQuery: (state, action: PayloadAction<string>) => {
      state.searchQuery = action.payload
    },
    setSelectedListingType: (state, action: PayloadAction<ListingType | 'all'>) => {
      state.selectedListingType = action.payload
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload
    },
    toggleFavorite: (state, action: PayloadAction<string>) => {
      const listingId = action.payload
      if (state.favorites.includes(listingId)) {
        state.favorites = state.favorites.filter(id => id !== listingId)
      } else {
        state.favorites.push(listingId)
      }
    },
    setViewMode: (state, action: PayloadAction<'list' | 'grid' | 'map'>) => {
      state.viewMode = action.payload
    },
    incrementListingViews: (state, action: PayloadAction<string>) => {
      const listingId = action.payload
      const listing = state.listings.find(l => l.id === listingId)
      if (listing) {
        listing.views += 1
      }
      if (state.currentListing?.id === listingId) {
        state.currentListing.views += 1
      }
    },
    incrementListingInquiries: (state, action: PayloadAction<string>) => {
      const listingId = action.payload
      const listing = state.listings.find(l => l.id === listingId)
      if (listing) {
        listing.inquiries += 1
      }
      if (state.currentListing?.id === listingId) {
        state.currentListing.inquiries += 1
      }
    },
    addListing: (state, action: PayloadAction<Listing>) => {
      state.listings.unshift(action.payload)
      state.totalCount += 1
    },
    updateListingInState: (state, action: PayloadAction<Listing>) => {
      const index = state.listings.findIndex(listing => listing.id === action.payload.id)
      if (index !== -1) {
        state.listings[index] = action.payload
      }
      if (state.currentListing?.id === action.payload.id) {
        state.currentListing = action.payload
      }
    },
    deleteListingFromState: (state, action: PayloadAction<string>) => {
      const listingId = action.payload
      state.listings = state.listings.filter(listing => listing.id !== listingId)
      state.searchResults = state.searchResults.filter(listing => listing.id !== listingId)
      state.favorites = state.favorites.filter(id => id !== listingId)
      state.totalCount -= 1
      if (state.currentListing?.id === listingId) {
        state.currentListing = null
      }
    },
    setCurrentDraft: (state, action: PayloadAction<Listing | null>) => {
      state.currentDraft = action.payload
    },
    clearDraft: (state) => {
      state.currentDraft = null
      state.draftLoading = false
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(createListing.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(createListing.fulfilled, (state, action) => {
        state.loading = false
        state.listings.unshift(action.payload)
        state.totalCount += 1
      })
      .addCase(createListing.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(fetchListings.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchListings.fulfilled, (state, action) => {
        state.loading = false
        state.listings = action.payload
        state.searchResults = action.payload
        state.totalCount = action.payload.length
      })
      .addCase(fetchListings.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(fetchListingById.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchListingById.fulfilled, (state, action) => {
        state.loading = false
        state.currentListing = action.payload
      })
      .addCase(fetchListingById.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(updateListing.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateListing.fulfilled, (state, action) => {
        state.loading = false
        const index = state.listings.findIndex(listing => listing.id === action.payload.id)
        if (index !== -1) {
          state.listings[index] = action.payload
        }
        if (state.currentListing?.id === action.payload.id) {
          state.currentListing = action.payload
        }
      })
      .addCase(updateListing.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(removeListing.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(removeListing.fulfilled, (state, action) => {
        state.loading = false
        const listingId = action.payload
        state.listings = state.listings.filter(listing => listing.id !== listingId)
        state.searchResults = state.searchResults.filter(listing => listing.id !== listingId)
        state.favorites = state.favorites.filter(id => id !== listingId)
        state.totalCount -= 1
        if (state.currentListing?.id === listingId) {
          state.currentListing = null
        }
      })
      .addCase(removeListing.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      .addCase(saveDraft.pending, (state) => {
        state.draftLoading = true
        state.error = null
      })
      .addCase(saveDraft.fulfilled, (state, action) => {
        state.draftLoading = false
        state.currentDraft = action.payload
      })
      .addCase(saveDraft.rejected, (state, action) => {
        state.draftLoading = false
        state.error = action.payload as string
      })
      .addCase(updateDraft.pending, (state) => {
        state.draftLoading = true
        state.error = null
      })
      .addCase(updateDraft.fulfilled, (state, action) => {
        state.draftLoading = false
        state.currentDraft = action.payload
      })
      .addCase(updateDraft.rejected, (state, action) => {
        state.draftLoading = false
        state.error = action.payload as string
      })
      .addCase(publishDraft.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(publishDraft.fulfilled, (state, action) => {
        state.loading = false
        state.listings.unshift(action.payload)
        state.totalCount += 1
        state.currentDraft = null
      })
      .addCase(publishDraft.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  }
})

export const {
  setListings,
  setSearchResults,
  setCurrentListing,
  setFilters,
  updateFilter,
  clearFilters,
  setSearchQuery,
  setSelectedListingType,
  setLoading,
  setError,
  toggleFavorite,
  setViewMode,
  incrementListingViews,
  incrementListingInquiries,
  addListing,
  updateListingInState,
  deleteListingFromState,
  setCurrentDraft,
  clearDraft
} = listingSlice.actions

export default listingSlice.reducer
